/**
 * Where Freya's floating launcher sits, and where her panel opens from it.
 *
 * The launcher is draggable on desktop (owner, 2026-09-28): it parks at the
 * bottom-right corner by default, but that corner is exactly where some pages
 * keep their own primary action, so operators may move it out of the way.
 * Everything here is pure (no React, no DOM beyond `localStorage`) so the
 * drag-vs-click threshold, the clamp and the storage fallback can be tested
 * without a layout engine — jsdom has none.
 *
 * A moved position is stored against the viewport edges it is NEAREST to, not
 * as absolute x/y: an icon parked 40px from the bottom-right corner stays 40px
 * from that corner when the window is resized, instead of drifting into the
 * middle of a wider screen or off the edge of a narrower one.
 */

/** The launcher is a 56px circle. */
export const LAUNCHER_SIZE = 56;
/** Today's default corner: 24px in from the right and the bottom. */
export const LAUNCHER_INSET = 24;
/**
 * Closest the launcher may get to any viewport edge. Not zero, so a sliver
 * of its shadow survives and it never reads as cut off by the window.
 */
export const EDGE_MARGIN = 8;
/**
 * A press that travels less than this is still a click. Hands shake and
 * trackpads skid a pixel or two on a firm tap; without the slack, a tap
 * that moved 2px would pick the icon up instead of opening the chat.
 */
export const DRAG_THRESHOLD = 5;
/**
 * Dropping the icon this close to its home corner puts it home and forgets
 * the stored position. This is the reset: double-click cannot be, because
 * its two clicks would open then close the panel on the way.
 */
export const SNAP_HOME_RADIUS = 16;

// The panel's own geometry — today's values, so an unmoved launcher opens
// its panel exactly where it always has (right 24, bottom 92).
export const PANEL_W = 380;
const PANEL_H = 520;
/** Space between the launcher and the panel edge that faces it. */
const PANEL_GAP = 12;
/** Closest the panel may get to a viewport edge (the old `100vw - 32px`). */
const PANEL_MARGIN = 16;
/** The old `100vh - 150px` height cap. */
const PANEL_CHROME = 150;
/** Shortest the panel gets when the icon sits mid-screen with little room. */
const PANEL_MIN_H = 240;

export const LAUNCHER_STORAGE_KEY = 'amassa.freya.launcher.v1';

export type Point = { left: number; top: number };
export type Viewport = { w: number; h: number };

/** Distance `x` from the `h` edge and `y` from the `v` edge, in CSS px. */
export type LauncherAnchor = {
  h: 'left' | 'right';
  x: number;
  v: 'top' | 'bottom';
  y: number;
};

export const DEFAULT_ANCHOR: LauncherAnchor = {
  h: 'right',
  x: LAUNCHER_INSET,
  v: 'bottom',
  y: LAUNCHER_INSET,
};

/** True once a press has travelled far enough to count as a drag. */
export function pastDragThreshold(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= DRAG_THRESHOLD;
}

/** Clamp one offset so the whole icon stays inside a span of `extent` px. */
function clampOffset(v: number, extent: number): number {
  // On a viewport too small to honour both margins the icon pins to the
  // near margin rather than producing an inverted range.
  const max = Math.max(EDGE_MARGIN, extent - LAUNCHER_SIZE - EDGE_MARGIN);
  return Math.min(Math.max(v, EDGE_MARGIN), max);
}

/** Keep a top-left position fully on-screen. */
export function clampPoint(p: Point, vp: Viewport): Point {
  return { left: clampOffset(p.left, vp.w), top: clampOffset(p.top, vp.h) };
}

/**
 * Keep an anchored position fully on-screen, on the same edges. Applied at
 * render time and never written back: shrink the window and the icon is
 * pushed in, grow it again and the icon returns to where it was put.
 */
export function clampAnchor(a: LauncherAnchor, vp: Viewport): LauncherAnchor {
  return { h: a.h, x: clampOffset(a.x, vp.w), v: a.v, y: clampOffset(a.y, vp.h) };
}

/** The clamped top-left corner an anchor resolves to in this viewport. */
export function anchorToPoint(a: LauncherAnchor, vp: Viewport): Point {
  const c = clampAnchor(a, vp);
  return {
    left: c.h === 'left' ? c.x : vp.w - LAUNCHER_SIZE - c.x,
    top: c.v === 'top' ? c.y : vp.h - LAUNCHER_SIZE - c.y,
  };
}

/** Re-express a top-left position against the edges its centre is nearest. */
export function pointToAnchor(p: Point, vp: Viewport): LauncherAnchor {
  const half = LAUNCHER_SIZE / 2;
  const right = p.left + half >= vp.w / 2;
  const bottom = p.top + half >= vp.h / 2;
  return {
    h: right ? 'right' : 'left',
    x: Math.round(right ? vp.w - LAUNCHER_SIZE - p.left : p.left),
    v: bottom ? 'bottom' : 'top',
    y: Math.round(bottom ? vp.h - LAUNCHER_SIZE - p.top : p.top),
  };
}

/**
 * Where a drop lands: clamped on-screen, anchored to its nearest edges, or
 * `null` (= the default corner, nothing stored) when it is dropped home.
 */
export function settleDrop(p: Point, vp: Viewport): LauncherAnchor | null {
  const a = pointToAnchor(clampPoint(p, vp), vp);
  const home =
    a.h === 'right' &&
    a.v === 'bottom' &&
    Math.hypot(a.x - LAUNCHER_INSET, a.y - LAUNCHER_INSET) <= SNAP_HOME_RADIUS;
  return home ? null : a;
}

// ── Persistence ──────────────────────────────────────────────────────

/**
 * Validate a stored value. Anything that is not exactly the shape we write
 * (an old format, a hand edit, a truncated write) is treated as absent, so
 * the worst a bad value can do is put the icon back in its default corner.
 */
export function parseAnchor(v: unknown): LauncherAnchor | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.h !== 'left' && o.h !== 'right') return null;
  if (o.v !== 'top' && o.v !== 'bottom') return null;
  if (typeof o.x !== 'number' || !Number.isFinite(o.x)) return null;
  if (typeof o.y !== 'number' || !Number.isFinite(o.y)) return null;
  return { h: o.h, x: o.x, v: o.v, y: o.y };
}

/**
 * The remembered position, or `null` for the default corner. Every storage
 * touch is wrapped: the accessor itself throws in some private windows and
 * with site data blocked, and a launcher that cannot remember its spot must
 * still render.
 */
export function readLauncherAnchor(): LauncherAnchor | null {
  try {
    const raw = window.localStorage.getItem(LAUNCHER_STORAGE_KEY);
    return raw ? parseAnchor(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Remember a position; `null` forgets it (the icon is home). */
export function writeLauncherAnchor(a: LauncherAnchor | null): void {
  try {
    if (a) window.localStorage.setItem(LAUNCHER_STORAGE_KEY, JSON.stringify(a));
    else window.localStorage.removeItem(LAUNCHER_STORAGE_KEY);
  } catch {
    // Quota, private mode, blocked storage: the icon stays where it was
    // dropped for this page's life and simply is not remembered.
  }
}

// ── The panel ────────────────────────────────────────────────────────

/** A fixed box, anchored to the edges nearest the launcher like the icon. */
export type PanelBox = LauncherAnchor & { width: number; height: number };

/**
 * Where the chat panel opens for a launcher at `icon`.
 *
 * It keeps the relationship it has always had with the icon — stacked on it,
 * flush with its outer edge — and only changes WHICH side: above or below,
 * whichever has more room, and aligned to the icon's right edge on the right
 * half of the screen, its left edge on the left half. An icon dragged to the
 * top-left therefore opens its panel downward and rightward instead of off
 * the top of the window. The result is then clamped on-screen; in the rare
 * squeeze where the panel cannot fit beside the icon it slides over it, and
 * the icon (painted later, same layer) stays clickable as the close control.
 */
export function panelBox(icon: Point, vp: Viewport): PanelBox {
  const width = Math.max(0, Math.min(PANEL_W, vp.w - 2 * PANEL_MARGIN));

  const roomAbove = icon.top - PANEL_GAP - PANEL_MARGIN;
  const roomBelow = vp.h - (icon.top + LAUNCHER_SIZE) - PANEL_GAP - PANEL_MARGIN;
  const above = roomAbove >= roomBelow;
  const cap = Math.min(PANEL_H, vp.h - PANEL_CHROME);
  const room = Math.max(above ? roomAbove : roomBelow, 0);
  const height = Math.max(0, Math.min(cap, room), Math.min(PANEL_MIN_H, cap));

  // `y` is measured from the edge the panel grows away from the icon along.
  const yMax = Math.max(PANEL_MARGIN, vp.h - PANEL_MARGIN - height);
  const y = Math.min(
    above ? vp.h - icon.top + PANEL_GAP : icon.top + LAUNCHER_SIZE + PANEL_GAP,
    yMax,
  );

  const right = icon.left + LAUNCHER_SIZE / 2 >= vp.w / 2;
  const xMax = Math.max(PANEL_MARGIN, vp.w - PANEL_MARGIN - width);
  const x = Math.min(
    Math.max(right ? vp.w - icon.left - LAUNCHER_SIZE : icon.left, PANEL_MARGIN),
    xMax,
  );

  return {
    h: right ? 'right' : 'left',
    x,
    v: above ? 'bottom' : 'top',
    y,
    width,
    height,
  };
}
